import { AuthModel } from './_models';
import { alertError } from '../../../app/helpers/alert';
import { refreshToken } from './_requests';

const AUTH_LOCAL_STORAGE_KEY = process.env.REACT_APP_LOCAL_STORAGE_AUTH || 'crego-auth';
const getAuth = (): AuthModel | undefined => {
    if (!localStorage) {
        return;
    }

    const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY);
    if (!lsValue) {
        return;
    }

    try {
        const auth: AuthModel = JSON.parse(lsValue) as AuthModel;
        if (auth) {
            return auth;
        }
    } catch (error) {
        console.error('AUTH LOCAL STORAGE PARSE ERROR', error);
    }
};

const setAuth = (auth: AuthModel) => {
    if (!localStorage) {
        return;
    }
    try {
        const lsValue = JSON.stringify(auth);
        localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue);
    } catch (error) {
        console.error('AUTH LOCAL STORAGE SAVE ERROR', error);
    }
};

const removeAuth = () => {
    if (!localStorage) {
        return;
    }

    try {
        localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY);
        localStorage.removeItem('nav_variables');
        localStorage.removeItem('silent_auth');
    } catch (error) {
        console.error('AUTH LOCAL STORAGE REMOVE ERROR', error);
    }
};

export function setupAxios(axios: any) {
    axios.defaults.headers.Accept = 'application/json';
    axios.interceptors.request.use(
        (config: {
            headers: {
                Authorization: string;
                'ngrok-skip-browser-warning': boolean;
                'x-app-name': string;
            };
        }) => {
            config.headers['x-app-name'] = process.env.REACT_APP_NAME || 'jupiter';
            const auth = getAuth();
            if (auth && auth.access) {
                config.headers.Authorization = 'Bearer ' + auth.access;
                config.headers['ngrok-skip-browser-warning'] = true;
            }

            return config;
        },
        (err: any) => Promise.reject(err)
    );

    axios.interceptors.response.use(
        (response: any) => response,
        async (error: any) => {
            const status = error?.response?.status;
            const isRefreshEndpoint = error?.config?.url?.includes('refresh');
            const auth = getAuth();

            if ((status === 401 || status === 403) && !isRefreshEndpoint) {
                try {
                    if (auth?.refresh) {
                        const res = await refreshToken(auth.refresh);
                        if (res.status === 200) {
                            setAuth(res.data);
                            error.config.headers.Authorization = `Bearer ${res.data.access}`;
                            return axios.request(error.config);
                        }
                    }
                } catch (refreshError) {}

                removeAuth();
                if (localStorage.getItem('silent_auth') !== 'true') {
                    alertError(
                        'Your login session has expired, please login again',
                        'Session Expired'
                    );
                }
                localStorage.removeItem('silent_auth');
                window.location.href = '/auth/login';
            }

            return Promise.reject(error);
        }
    );
}

export const extractAndSaveNavVariables = () => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        const urlParams = new URLSearchParams(window.location.search);
        const navVariables: Record<string, string> = {};

        urlParams.forEach((value, key) => {
            if (key.startsWith('nav_')) {
                const navKey = key.replace(/^nav_/, '');
                navVariables[navKey] = value;
            }
        });

        if (Object.keys(navVariables).length > 0) {
            const existingNavVars = localStorage.getItem('nav_variables');
            let mergedNavVars: Record<string, string> = {};

            if (existingNavVars) {
                try {
                    mergedNavVars = JSON.parse(existingNavVars);
                } catch (e) {
                    mergedNavVars = {};
                }
            }

            mergedNavVars = {
                ...mergedNavVars,
                ...navVariables,
            };

            localStorage.setItem('nav_variables', JSON.stringify(mergedNavVars));
        }
    } catch (error) {
        console.error('Error extracting nav variables:', error);
    }
};
export const isNavItemEnabled = (navKey: string, defaultEnabled: boolean = true): boolean => {
    if (typeof window === 'undefined' || !localStorage) {
        return defaultEnabled;
    }

    try {
        const navVars = localStorage.getItem('nav_variables');
        if (!navVars) {
            return defaultEnabled;
        }

        const navVariables = JSON.parse(navVars);

        if (!Object.prototype.hasOwnProperty.call(navVariables, navKey)) {
            return defaultEnabled;
        }

        const value: any = navVariables[navKey];

        if (typeof value === 'boolean') {
            return value;
        }

        const lowerValue = String(value).toLowerCase().trim();

        return !(lowerValue === 'false' || lowerValue === '0' || lowerValue === 'no');
    } catch (error) {
        console.error('Error getting nav variable:', error);
        return defaultEnabled;
    }
};

export { getAuth, setAuth, removeAuth, AUTH_LOCAL_STORAGE_KEY };
